import { secondaryButton, type RGB } from '@tma.js/sdk-react'
import { useEffect } from 'react'
import { useLatest } from '@/shared/lib'
import { blendOver, parseCssColor, readColorToken, readCssToken, toRgb } from './css-color'

export type SecondaryButtonConfig = {
  text: string
  onClick: () => void
  isVisible?: boolean
  isEnabled?: boolean
  isLoading?: boolean
  /** Деструктивная альтернатива («Отказаться от подарка») — цветом `tg-destructive`. */
  isDestructive?: boolean
}

type Tone = 'default' | 'destructive'

let tone: Tone = 'default'
/** Цвет нижней панели, если экран держит свои цвета (`useChromeOverride`); иначе панель — `tg-bg`. */
let surface: RGB | null = null

/**
 * Фон кнопки — как в макете (`BottomBar`): полупрозрачный `tg-segment-bg`
 * поверх нижней панели. SDK принимает только непрозрачный `#rrggbb`, поэтому
 * цвет смешивается здесь: светлая iOS-палитра — `#eeeef0` на белой панели
 * (сборка пишет альфу 0.12 как `1f`), тёмная — `#323235` на `#1c1c1d`. Та же
 * формула даёт кнопку кита на старом клиенте (`bg-tg-segment-bg` на `bg-tg-bg`)
 * — цвет совпадает.
 */
const readBackgroundColor = (): RGB | null => {
  const overlay = parseCssColor(readCssToken('--tg-segment-bg'))
  const base = parseCssColor(surface ?? readCssToken('--tg-bg'))

  return overlay && base ? toRgb(blendOver(overlay, base)) : null
}

/** Нативная SecondaryButton есть — Mini Apps 7.10 и новее; смонтирована в `initTelegram`. */
export const isSecondaryButtonNative = (): boolean => secondaryButton.isMounted()

/**
 * Нижняя панель экрана со своими цветами (`useChromeOverride`): фон кнопки
 * смешивается с ней. `null` — панель снова `tg-bg`. Задаёт `shared/telegram/chrome`.
 */
export const setSecondaryButtonSurface = (color: RGB | null): void => {
  surface = color
}

/**
 * Перекрасить кнопку под текущую тему: вызывается при смене темы
 * (`syncChromeColors`, `themeChanged`) и при смене тона кнопки. Фон задаём
 * сами — без него Telegram красит вторую кнопку цветом главной, и на экране
 * две одинаковые синие кнопки.
 */
export const refreshSecondaryButtonColors = (): void => {
  if (!isSecondaryButtonNative()) {
    return
  }

  // Текст — из тех же токенов, что красят экран: совпадает и с палитрой клиента, и с ручной темой.
  const textColor = readColorToken(tone === 'destructive' ? '--tg-destructive' : '--tg-link')
  const bgColor = readBackgroundColor()

  if (textColor || bgColor) {
    secondaryButton.setParams.ifAvailable({ ...(textColor && { textColor }), ...(bgColor && { bgColor }) })
  }
}

/**
 * Вторая кнопка экрана — нативная SecondaryButton Telegram над MainButton.
 * Только альтернатива тому же решению (отказаться, закрыть) и только там, где
 * она есть в таблице экранов CLAUDE.md.
 *
 * Возвращает `true`, если кнопку рисует Telegram. `false` — клиент старше
 * 7.10, кнопки нет: экран рисует кнопку кита сам (`SecondaryButton` из
 * `shared/ui` делает это за него), без падения экрана.
 */
export const useSecondaryButton = (config: SecondaryButtonConfig | null): boolean => {
  const isNative = isSecondaryButtonNative()
  const onClick = useLatest(config?.onClick)
  const text = config?.text ?? ''
  const isVisible = config !== null && (config.isVisible ?? true) && text !== ''
  const isEnabled = config?.isEnabled ?? true
  const isLoading = config?.isLoading ?? false
  const nextTone: Tone = config?.isDestructive ? 'destructive' : 'default'

  useEffect(() => {
    if (!isNative) {
      return
    }

    tone = nextTone
    // Цвета — до показа: иначе кнопка на миг появится цветом главной.
    refreshSecondaryButtonColors()
    secondaryButton.setParams.ifAvailable(
      isVisible
        ? { text, isVisible, isEnabled, isLoaderVisible: isLoading, position: 'top' }
        : { isVisible: false, isLoaderVisible: false },
    )
  }, [isNative, text, isVisible, isEnabled, isLoading, nextTone])

  useEffect(() => {
    if (!isNative) {
      return
    }

    const subscription = secondaryButton.onClick.ifAvailable(() => onClick.current?.())

    return () => {
      if (subscription.ok) {
        subscription.data()
      }

      secondaryButton.setParams.ifAvailable({ isVisible: false, isLoaderVisible: false })
    }
  }, [isNative, onClick])

  return isNative
}
